// NPM
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useHistory } from "react-router-dom";
import { isNil, isEmpty, omitBy, omit, pick } from "lodash";
import { useTranslation } from "react-i18next";

// HOOKS
import useQuery from "Hooks/query.hook";
import { useSession } from "Hooks/index";
import usePagination from "Hooks/pagination.hook";

// HELPERS
import { toaster } from "Helpers/toaster";

// SERVICES
import {
  fetchOrderSettingList,
  createOrderSetting,
  updateOrderSetting,
} from "Services/order-setting.service";
import { addCustomerSetting } from "Services/customer-settings.service";

// STYLE
// import '../../order-management/order-management.scss';

//EXCEL
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// ANT COMPONENTS
import { SettingListComponent } from "Components/order-management/shared/settings-list";
import { Tabs } from "@life-line-lab/components-react";
import moment from "moment";
import { getXlFieldsBySettingType } from "./setting-xl-helper";
import { getEnumOfOrderSettings, getIndexOfOrderSettings } from "Helpers/order-settings-helper";
import { OrderSettingsEnum } from "Data/settings";
import {
  createCustomerRateTableSetting,
  fetchCustomerRateTableList,
  updateCustomerRateTableSetting,
} from "Services/customer-rate-table.services";

const defaultSorting = {
  sortBy: "createdAt",
  mode: "DESC",
};

const OrderProcessingSettings: React.FC<any> = () => {
  const { t } = useTranslation();
  const { org } = useSession();
  const history = useHistory();
  const query = useQuery();
  const page = query.get("page") || 1;
  const tabKey = query.get("tabKey") || "0";
  const forceRefresh = query.get("refresh") || null;

  const [response, setResponse] = useState([]);
  const [hideModal, setHideModal] = useState<any>(false);
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentListCount, setCurrentListCount] = useState(0);
  const { paginationComponent, itemPerPage, queryParams, setQueryParams } = usePagination({
    currentListCount,
  });

  const initialFilters = {
    orgID: org._id,
    type: getEnumOfOrderSettings(tabActiveKey),
    pagination: {
      offset: queryParams.offset,
      limit: itemPerPage,
    },
  };
  const [filters, setFilters] = useState<any>(initialFilters);

  const tabKeyPaginationProps = {
    tabKey: tabKey,
    pathWithPage: {
      withTabKey: `/order-management/settings?tabKey=${tabKey}&page=${page}`,
      withoutTabKey: `/order-management/settings?page=${page}`,
    },
    pathWithoutPage: {
      withTabKey: `/order-management/settings?tabKey=${tabKey}`,
      withoutTabKey: "/order-management/settings",
    },
  };

  const onFilter = (filterInput) => {
    if (isEmpty(filterInput)) {
      setFilters(initialFilters);
      return;
    }
    const filterValue = {
      ...filters,
      orFields: filterInput.orFields || null,
    };
    setFilters(filterValue);
  };

  useEffect(() => {
    if (!isEmpty(filters)) {
      handleFetchOrderSettingsList();
    }
  }, [filters]);

  const handleFetchOrderSettingsList = async () => {
    setIsLoading && setIsLoading(true);

    let responseData: any = {};

    if (filters.type === OrderSettingsEnum.CUSTOMER_RATE_TABLE) {
      const payload = {
        ...filters,
        includeInactive: true,
      };

      responseData = await fetchCustomerRateTableList(payload, filters.pagination, defaultSorting);
    } else {
      responseData = await fetchOrderSettingList(omitBy(filters, isNil));
    }

    if (responseData && !responseData.error) {
      setResponse(responseData);
      setCurrentListCount(responseData.length);
    } else {
      toaster("error", responseData.error.message);
    }

    setIsLoading && setIsLoading(false);
  };

  // temporary solution on updating data
  if (forceRefresh) {
    history.push(`/order-management/settings?tabKey=${tabKey}`);
  }

  const handleCreateVersion = async (data) => {
    data.type = getEnumOfOrderSettings(tabKey);

    let responseData: any = {};

    if (data.type === OrderSettingsEnum.CUSTOMER_RATE_TABLE) {
      const payload = {
        ...data,
        orgID: org?._id,
      };

      responseData = await createCustomerRateTableSetting(payload);
    } else {
      responseData = await createOrderSetting(data);
    }

    if (!isNil(responseData) && responseData.success) {
      setHideModal((hideModal) => {
        return !hideModal;
      });
      toaster("success", t("toaster.createVersionSuccess"));
      handleFetchOrderSettingsList();
    } else {
      toaster("error", t("toaster.createFailed"));
    }
  };

  const handleUpdateVersion = async (data) => {
    data.type = getEnumOfOrderSettings(tabKey);

    let responseData: any = {};

    if (data.type === OrderSettingsEnum.CUSTOMER_RATE_TABLE) {
      const payload = {
        ...data,
        orgID: org?._id,
      };

      responseData = await updateCustomerRateTableSetting(payload);
    } else {
      responseData = await updateOrderSetting(data);
    }

    if (!isNil(responseData) && responseData.success) {
      setHideModal((hideModal) => {
        return !hideModal;
      });
      toaster("success", t("toaster.updateVersionSuccess"));
      handleFetchOrderSettingsList();
    } else {
      toaster("error", t("toaster.updateFailed"));
    }
  };

  const handleAddCustomerSubmit = async (data) => {
    let responseData: any = await addCustomerSetting(data);

    if (!isNil(responseData) && responseData.success) {
      setHideModal((hideModal) => {
        return !hideModal;
      });
      toaster("success", t("toaster.addCustomerSuccess"));
    } else {
      toaster("error", t("toaster.addFailed"));
    }
  };

  const handleDownloadExcelFile = (
    tableData: any[],
    fileName: string,
    columns: string[],
    settingType,
  ) => {
    const workbook = XLSX.utils.book_new();

    // this is true for any setting with the same "name" field and uses "areaName" as sheetName
    let skipNameCheck = true;

    // this is true if there is a need for custom sheet name
    let enableCustomSheet = true;

    // this is required for names that can exist as duplicates
    let duplicatedNameArray: any = [];

    // generate rows of data
    const generateSheetData = (item) => {
      let flattenedVersions: any = [];

      // if custom sheet is enabled, there are multiple versions
      if (enableCustomSheet) {
        // loop through each version and flatten the nested objects
        item.versions.forEach((version) => {
          const versionData = version.data || {};
          const versionMaxDimension = version.maxDimension || {};
          const versionSurcharge = version.surcharge || {};
          let flattenedVersion;

          item.name != "rate table"
            ? (flattenedVersion = {
                ...version,
                maxWeight: versionData.maxWeight,
                validFrom: moment(versionData.validFrom).format("DD-MM-YYYY HH:MM:SS"),
                validTo: moment(versionData.validTo).format("DD-MM-YYYY HH:MM:SS"),
                surcharge: versionSurcharge.fixedAmount || versionSurcharge.percentage,
                length: versionMaxDimension.length,
                width: versionMaxDimension.width,
                height: versionMaxDimension.height,
              })
            : versionData.rates.forEach((rate) => {
                flattenedVersion = {
                  ...versionData,
                  ...rate,
                  customerRates: !isEmpty(version?.customerRates) ? version?.customerRates : null,
                  code: version.code,
                  validFrom: moment(versionData.validFrom).format("DD-MM-YYYY HH:MM:SS"),
                  validTo: moment(versionData.validTo).format("DD-MM-YYYY HH:MM:SS"),
                  version: version.version,
                };
              });

          let filteredObj = getXlFieldsBySettingType[settingType](flattenedVersion);

          // console.log("flattenedVersion", flattenedVersion);
          // console.log("filteredObj", filteredObj);

          flattenedVersions.push(filteredObj);
        });
      } else {
        // these are single sheet items
        const filteredObj = item?.map((setting) => getXlFieldsBySettingType[settingType](setting));
        flattenedVersions = [...filteredObj];
      }

      return flattenedVersions;
    };

    // if custom sheet name is required
    const customSheetName = (item) => {
      // in the case BE sends the incorrect data, skip that data
      if (skipNameCheck || item.name == settingType.toLowerCase().replace("_", " ")) {
        let doesDuplicateNameExist = false;

        // the name (for sheetName reference) can be item.name or item.areaName
        //   !! this can break if another field besides these two are used
        let alternateName = item?.name ?? item?.areaName;

        // find if there is a duplicated name
        if (skipNameCheck === true) {
          for (let i = 0; i < duplicatedNameArray.length; i++) {
            if (duplicatedNameArray[i] == alternateName) {
              doesDuplicateNameExist = true;
            }
          }
        }

        // if there is, bind the name with the item code, which is definitely unique
        if (doesDuplicateNameExist) {
          alternateName = `${alternateName} - ${item?.code?.replace('"', "")}`;
        }

        // put this into the array, for subsequent comparison
        duplicatedNameArray.push(alternateName);

        // use the alternate name (only if skipNameCheck is true)
        const sheetName =
          item.name != "rate table"
            ? alternateName?.substring(0, 30)
            : item.name == "rate table"
            ? item?.data?.locations[0]?.areaName?.substring(0, 30)
            : item.areaName?.substring(0, 30);

        // Initialize a new array to hold the flattened versions
        let flattenedVersions: any = [];

        // generate flattened version
        flattenedVersions = generateSheetData(item);

        // Convert the flattened versions to a worksheet
        const worksheet = XLSX.utils.json_to_sheet(flattenedVersions, {
          header: columns,
        });

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      } else {
        return;
      }
    };

    // if only one sheet is needed
    const singleSheet = (item) => {
      // Initialize a new array to hold the flattened versions
      let flattenedVersions: any = [];

      // generate flattened version
      flattenedVersions = generateSheetData(item);

      // Convert the flattened versions to a worksheet
      const worksheet = XLSX.utils.json_to_sheet(flattenedVersions);

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet);
    };

    // Write the workbook to a buffer and download it as a file
    try {
      // if the data (e.g. Rate Table) happens to have another type in its name field (e.g. name: insurance), prevent this data from being input into the excel sheet
      switch (settingType) {
        case "RATE_TABLE":
          skipNameCheck = false;
          break;
        case "ISSUE_TYPE":
        case "TYPE_OF_GOODS":
          enableCustomSheet = false;
          break;
        default:
      }

      // main mapping process
      enableCustomSheet ? tableData.map((item) => customSheetName(item)) : singleSheet(tableData);

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const fileData = new Blob([excelBuffer], { type: "application/vnd.ms-excel" });
      saveAs(fileData, `${fileName}.xlsx`);
    } catch (error) {
      console.error(error);
      toaster("error", "An error occurred while trying to download the file.");
    }
  };

  useEffect(() => {
    return () => {
      setResponse([]);
    };
  }, [tabActiveKey]);

  const settingListProps = {
    isLoading,
    onFilter,
    tabName: getEnumOfOrderSettings(tabActiveKey),
    data: response,
    hideModal,
    onAddCustomer: handleAddCustomerSubmit,
    onCreateVersion: handleCreateVersion,
    onUpdateVersion: handleUpdateVersion,
    onDownloadExcel: handleDownloadExcelFile,
    handleRefresh: handleFetchOrderSettingsList,
  };

  const tabs = [
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.SPECIAL_CARE),
      label: t("label.specialCare"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.SPECIAL_CARE}
          {...settingListProps}
        />
      ),
    },
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.SPECIAL_CONDITION),
      label: t("label.specialConditions"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.SPECIAL_CONDITION}
          {...settingListProps}
        />
      ),
    },
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.SERVICE_TYPE),
      label: t("label.serviceType"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.SERVICE_TYPE}
          {...settingListProps}
        />
      ),
    },
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.DELIVERY_TYPE),
      label: t("label.deliveryType"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.DELIVERY_TYPE}
          {...settingListProps}
        />
      ),
    },
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.LOCATION),
      label: t("label.locations"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.LOCATION}
          {...settingListProps}
        />
      ),
    },
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.RATE_TABLE),
      label: t("label.rateTable"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.RATE_TABLE}
          {...settingListProps}
        />
      ),
    },
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.CUSTOMER_RATE_TABLE),
      label: t("label.customerRateTable"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.CUSTOMER_RATE_TABLE}
          // cant use isCustomerPage here, because it affects something else
          {...settingListProps}
        />
      ),
    },
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.ISSUE_TYPE),
      label: t("label.issueType"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.ISSUE_TYPE}
          {...settingListProps}
        />
      ),
    },
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.TYPE_OF_GOODS),
      label: t("label.typeOfGoods"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.TYPE_OF_GOODS}
          {...settingListProps}
        />
      ),
    },
    {
      tabKey: getIndexOfOrderSettings(OrderSettingsEnum.INSURANCE),
      label: t("label.insurance"),
      component: (
        <SettingListComponent
          tab={OrderSettingsEnum.INSURANCE}
          {...settingListProps}
        />
      ),
    },
  ];

  /**
   * function to change url path when tab changes
   * @param {string} key - tabKey
   */
  const handleTabClick = (key: string) => {
    let path = "";
    if (key === "0") {
      path = page ? `/order-management/settings?page=${page}` : `/order-management/settings`;
    } else {
      path = page
        ? `/order-management/settings?tabKey=${key}&page=${page}`
        : `/order-management/settings?tabKey=${key}`;
    }
    setFilters({ ...initialFilters, type: getEnumOfOrderSettings(key) });
    setTabActiveKey(key);
    history.push(path);
  };

  return (
    <div className="order-processing settings">
      <Helmet>
        <title>{t("pageTitle.orderProcessing")}</title>
      </Helmet>
      <div className="title mb-5">{t("title.orderProcessing")}</div>
      <React.Fragment>
        <Tabs
          destroyInactiveTabPane={true}
          onTabClick={handleTabClick}
          defaultActiveKey="0"
          activeKey={tabActiveKey}
          tabBarGutter={25}
          tabs={tabs}
        />
      </React.Fragment>
      <div className="my-30">{paginationComponent}</div>
    </div>
  );
};

export default OrderProcessingSettings;
